import { apiError, travelEsimClient } from '@/lib/api/ts-rest';
import { createLogger } from '@/lib/logger';
import { queryKeys } from '@/lib/query-keys';
import type {
  TravelEsimTariffInput,
  UpdateTravelEsimTariffBody,
} from '@octave/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useTravelEsimTariffActions');

/** Grid, destinations and quotes all read the current grid. */
function useInvalidateGrid() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.catalog.travelEsimRoot() });
  };
}

/** Octave admin: add an offer to the current grid. */
export function useCreateTravelEsimTariff() {
  const invalidate = useInvalidateGrid();
  return useMutation({
    mutationFn: async (body: TravelEsimTariffInput) => {
      const res = await travelEsimClient.createTravelEsimTariff({ body });
      if (res.status !== 201) throw apiError(res, "L'ajout du forfait a échoué");
      return res.body;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Forfait ajouté');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "L'ajout du forfait a échoué");
      log.error('Create travel eSIM tariff error', error);
    },
  });
}

/** Octave admin: change an offer of the current grid. */
export function useUpdateTravelEsimTariff() {
  const invalidate = useInvalidateGrid();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTravelEsimTariffBody & { id: string }) => {
      const res = await travelEsimClient.updateTravelEsimTariff({ params: { id }, body });
      if (res.status !== 200) throw apiError(res, 'La modification du forfait a échoué');
      return res.body;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Forfait modifié');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'La modification du forfait a échoué',
      );
      log.error('Update travel eSIM tariff error', error);
    },
  });
}

/** Octave admin: remove an offer from the current grid. */
export function useDeleteTravelEsimTariff() {
  const invalidate = useInvalidateGrid();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await travelEsimClient.deleteTravelEsimTariff({ params: { id } });
      if (res.status !== 200) throw apiError(res, 'La suppression du forfait a échoué');
      return res.body;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Forfait supprimé');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'La suppression du forfait a échoué',
      );
      log.error('Delete travel eSIM tariff error', error);
    },
  });
}
